import type { Theme } from '@mui/material';

const WHITE_CONTRAST = 'white'; // #fff
const BLACK_CONTRAST = 'black';

const colorSchemes = (): Theme['colorSchemes'] => {
  const error = {
    main: '#f04465', // Red_01
    light: '#F17297', // Red_03
    bgLightHover: '#FAC7D6', // Red_06
    bgLight: '#FCE3EA', // Red_07
    opacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.20)',
  };

  const warning = {
    main: '#ffc647', // Yellow_01
    bgLightHover: '#FFEFCA', // Yellow_06
    bgLight: '#FFF7E5', // Yellow_07
    contrastText: WHITE_CONTRAST,
    opacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.20)',
  };

  const info = {
    main: '#008EDD', // Blue_01
    contrastText: WHITE_CONTRAST,
    opacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.20)', // Blue_05
  };

  const success = {
    main: '#29A072', // Green_01
    bgLightHover: '#C0FFE5', // Green_06
    bgLight: '#D0FFEC', // Green_07
    contrastText: WHITE_CONTRAST,
    opacity: 'rgba(145,255,210,0.2)',
  };

  return {
    light: {
      palette: {
        primary: {
          main: '#02151E',
          light: '#A379FF',
          dark: '#E7F0F3',
          contrastText: 'white', //`rgb(var(--mui-mainColorChannels-light) / 0.8)`,
          lightOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.38)',
        },
        secondary: {
          main: '#8A8D93',
          light: '#A1A4A9',
          dark: '#7C7F84',
          contrastText: BLACK_CONTRAST,
          lighterOpacity:
            'rgb(var(--mui-palette-secondary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.24)',
        },
        error,
        warning,
        info,
        success,
        text: {
          primary: `#02151E`,
          secondary: `#505D63`,
          disabled: `#B1BBBF`,
        },
        divider: `#CFD9DC`,
        background: {
          default: '#F5F5F5', // Used by MUI automatically
          disabled: '#E5E9EB',
          paper: 'white', // Used by MUI automatically
          paperChannel: '255 255 255',
          dark: '#02151E',
          lightGray: '#b2bec1',
          tableHeader: '#EDEDED',
          page: '#EDEDED',
          pageGradient: 'linear-gradient(108deg, #FFF 2.25%, #E7F0F3 108.15%)',
          black1: `#02151E`,
        },
        action: {
          active: `rgb(var(--mui-mainColorChannels-light) / 0.6)`,
          hover: `rgb(var(--mui-mainColorChannels-light) / 0.04)`,
          selected: `rgb(var(--mui-mainColorChannels-light) / 0.08)`,
          disabled: `rgb(var(--mui-mainColorChannels-light) / 0.3)`,
          focus: `rgb(var(--mui-mainColorChannels-light) / 0.1)`,
          focusOpacity: 0.1,
          activeChannel: 'var(--mui-mainColorChannels-light)',
        },
        Tooltip: {
          bg: '#1A0E33',
          text: '#FFFFFF',
        },
        TableCell: {
          border: 'var(--mui-palette-divider)',
        },
        Input: {
          border: `rgb(var(--mui-mainColorChannels-light) / 0.22)`,
        },
        mainShadowChannel: '46 38 61',
      },
    },
    dark: {
      palette: {
        primary: {
          main: '#FFFFFF',
          light: '#A379FF',
          dark: '#02151E',
          lightOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.38)',
        },
        secondary: {
          main: '#8A8D93',
          light: '#A1A4A9',
          dark: '#7C7F84',
          contrastText: WHITE_CONTRAST,
          lighterOpacity:
            'rgb(var(--mui-palette-secondary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.24)',
        },
        error,
        warning,
        info,
        success,
        text: {
          primary: `#FFF`,
          secondary: `#B1BBBF`,
          disabled: `#595959`,
        },
        divider: '#404244',
        background: {
          default: '#17222c',
          disabled: '#3F3F3F',
          paper: '#192a35',
          paperChannel: '49 45 75',
          dark: '#F5F5F5',
          lightGray: '#b2bec1',
          tableHeader: '#121B23',
          page: '#121B23',
          pageGradient: undefined,
          black1: `#02151E`,
        },
        action: {
          active: `rgb(var(--mui-mainColorChannels-dark) / 0.6)`,
          hover: `rgb(var(--mui-mainColorChannels-dark) / 0.04)`,
          selected: `rgb(var(--mui-mainColorChannels-dark) / 0.08)`,
          disabled: `rgb(var(--mui-mainColorChannels-dark) / 0.3)`,
          focus: `rgb(var(--mui-mainColorChannels-dark) / 0.1)`,
          focusOpacity: 0.1,
          activeChannel: 'var(--mui-mainColorChannels-dark)',
        },
        Tooltip: {
          bg: '#F7F4FF',
          text: '#312D4B',
        },
        TableCell: {
          border: 'var(--mui-palette-divider)',
        },
        Input: {
          border: `rgb(var(--mui-mainColorChannels-dark) / 0.22)`,
        },
        mainShadowChannel: '19 17 32',
      },
    },
  } as unknown as Theme['colorSchemes'];
};

export default colorSchemes;
